import { getBusinessClient } from '@beatq/sdk-client';
import {
  mapErrorCode,
  readDataFromEnvelope,
  readEntityFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';
import { persistablePolicyRules, type PolicyRule } from './policyRules.js';

export type { PolicyRule } from './policyRules.js';
export { buildPolicyRules, persistablePolicyRules } from './policyRules.js';

type ApiErr = { error?: { code: string; message?: string }; message?: string };

export type Policy = {
  id?: string;
  name?: string | null;
  status?: string | null;
  scopeType?: string | null;
  scopeId?: string | null;
  enforcement?: string | null;
  assignmentCount?: number | null;
  limitMinor?: number | null;
  currency?: string | null;
  rules?: PolicyRule[];
  isActive?: boolean | null;
};

export type ApprovalChainStep = {
  tier: number;
  approverRoleCodes?: string[];
  approverEmployeeIds?: string[];
  thresholdMinor?: number;
  escalationMinutes?: number;
};

export type ApprovalChain = {
  id?: string;
  name?: string | null;
  scopeType?: string | null;
  scopeId?: string | null;
  steps?: ApprovalChainStep[] | unknown;
  thresholdMinor?: number | null;
  approverIds?: string[] | null;
  escalationHours?: number | null;
  status?: string | null;
  isActive?: boolean | null;
};

export type ApprovalRequest = {
  id?: string;
  reference?: string | null;
  status?: string | null;
  state?: string | null;
  requesterName?: string | null;
  requestedByEmployeeId?: string | null;
  requestedBy?: string | null;
  requestedAt?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  costCentreId?: string | null;
  amountMinor?: number | null;
  currency?: string | null;
  summary?: string | null;
  justification?: string | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  decidedAt?: string | null;
  decidedBy?: string | null;
  decidedByName?: string | null;
  triggerReasons?: string[] | null;
  basketSummary?: Record<string, unknown> | null;
  subjectSummary?: {
    kind?: string;
    id?: string | null;
    amountMinor?: number;
    currency?: string;
    justification?: string | null;
    costCentreId?: string | null;
  } | null;
  decisionHistory?: Array<{
    stepNumber: number;
    approverEmployeeId: string;
    state: string;
    decidedAt?: string | null;
    decisionNote?: string | null;
  }> | null;
  steps?: Array<{
    stepNumber?: number;
    approverEmployeeId?: string;
    state?: string;
    decidedAt?: string | null;
    decisionNote?: string | null;
  }> | null;
  clockPaused?: boolean | null;
  clockPausedAt?: string | null;
  pauseRemainingMs?: number | null;
  questions?: Array<{
    id?: string;
    body?: string;
    askedAt?: string;
    askedByEmployeeId?: string;
  }> | null;
};

export type ApprovalDelegation = {
  id?: string;
  fromEmployeeId?: string;
  toEmployeeId?: string;
  startsAt?: string;
  endsAt?: string;
  isActive?: boolean;
  revokedAt?: string | null;
};

export type SimulationResult = {
  mode?: string | null;
  outcome?: string | null;
  requiresApproval?: boolean | null;
  violations?: Array<{ ruleType: string; message: string; sourceScope?: string }>;
  effectiveLimits?: Record<string, number | string | boolean | null>;
  matchedOrderCount?: number | null;
  blockedOrderCount?: number | null;
  wouldBlock?: number | null;
  wouldRequireApproval?: number | null;
  ordersAnalysed?: number | null;
  periodDays?: number | null;
  topAffectedEmployees?: Array<{ employeeId: string; count: number }>;
  message?: string | null;
  policyId?: string | null;
  policyName?: string | null;
};

export type PolicyVersion = {
  version: number;
  current?: boolean;
  name?: string | null;
  scopeType?: string | null;
  scopeId?: string | null;
  enforcement?: string | null;
  rules?: PolicyRule[];
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
};

export type PolicyWriteBody = {
  name: string;
  scopeType?: string;
  scopeId?: string | null;
  enforcement?: string;
  rules: PolicyRule[];
};

export class BusinessApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'BusinessApiError';
    this.code = code;
  }
}

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? e?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function sdkErrorCode(error: unknown): string | undefined {
  return (error as ApiErr)?.error?.code;
}

function throwSdkError(error: unknown): never {
  throw new BusinessApiError(sdkErrorMessage(error), sdkErrorCode(error));
}

function enrichPolicy(policy: Policy): Policy {
  const rules = policy.rules ?? [];
  const perOrder = rules.find((r) => r.ruleType === 'PER_ORDER_CAP')?.moneyValueMinor;
  return {
    ...policy,
    status: policy.status ?? (policy.isActive === false ? 'INACTIVE' : 'ACTIVE'),
    limitMinor: policy.limitMinor ?? perOrder ?? null,
    rules,
  };
}

export function policyFromEnvelope(envelope: unknown): Policy | undefined {
  const policy =
    readObjectFieldFromEnvelope<Policy>(envelope, ['policy']) ??
    readDataFromEnvelope<Policy>(envelope);
  return policy ? enrichPolicy(policy) : undefined;
}

export function policiesFromEnvelope(envelope: unknown): Policy[] {
  return readListFromEnvelope<Policy>(envelope, ['policies', 'items']).map(enrichPolicy);
}

export function approvalChainFromEnvelope(envelope: unknown): ApprovalChain | undefined {
  return (
    readObjectFieldFromEnvelope<ApprovalChain>(envelope, ['chain']) ??
    readDataFromEnvelope<ApprovalChain>(envelope)
  );
}

export function approvalChainsFromEnvelope(envelope: unknown): ApprovalChain[] {
  return readListFromEnvelope<ApprovalChain>(envelope, ['chains', 'items']);
}

function isoDate(value: unknown): string | null {
  if (typeof value === 'string' && value) return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  return null;
}

function stringList(value: unknown): string[] | null {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) return [value];
  return null;
}

function enrichApproval(approval: ApprovalRequest): ApprovalRequest {
  const raw = approval as ApprovalRequest & { expiresAt?: string | Date | null };
  const basket =
    approval.basketSummary && typeof approval.basketSummary === 'object'
      ? approval.basketSummary
      : null;
  return {
    ...approval,
    status: approval.status ?? approval.state ?? null,
    justification: approval.justification ?? approval.subjectSummary?.justification ?? null,
    amountMinor: approval.amountMinor ?? approval.subjectSummary?.amountMinor ?? null,
    currency: approval.currency ?? approval.subjectSummary?.currency ?? 'GBP',
    costCentreId: approval.costCentreId ?? approval.subjectSummary?.costCentreId ?? null,
    requestedByEmployeeId: approval.requestedByEmployeeId ?? approval.requestedBy ?? null,
    requestedAt:
      isoDate(approval.requestedAt) ?? isoDate(approval.createdAt) ?? approval.requestedAt ?? null,
    expiresAt: isoDate(raw.expiresAt) ?? approval.expiresAt ?? null,
    triggerReasons: stringList(approval.triggerReasons) ?? approval.triggerReasons ?? null,
    basketSummary: basket,
    clockPaused: Boolean(approval.clockPaused ?? approval.clockPausedAt),
    questions: Array.isArray(approval.questions)
      ? approval.questions
      : (approval.questions ?? null),
  };
}

export function approvalFromEnvelope(envelope: unknown): ApprovalRequest | undefined {
  const approval =
    readObjectFieldFromEnvelope<ApprovalRequest>(envelope, ['approval', 'request']) ??
    readDataFromEnvelope<ApprovalRequest>(envelope);
  return approval ? enrichApproval(approval) : undefined;
}

export function approvalsFromEnvelope(envelope: unknown): ApprovalRequest[] {
  return readListFromEnvelope<ApprovalRequest>(envelope, ['approvals', 'items']).map(
    enrichApproval,
  );
}

export function delegationsFromEnvelope(envelope: unknown): ApprovalDelegation[] {
  return readListFromEnvelope<ApprovalDelegation>(envelope, ['delegations', 'items']);
}

export function simulationFromEnvelope(envelope: unknown): SimulationResult | undefined {
  return (
    readEntityFromEnvelope<SimulationResult>(envelope, ['result']) ??
    readDataFromEnvelope<SimulationResult>(envelope)
  );
}

export async function listPolicies() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/policies');
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function createPolicy(body: PolicyWriteBody) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/policies', {
    body: { ...body, rules: persistablePolicyRules(body.rules ?? []) } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function updatePolicy(policyId: string, body: Partial<PolicyWriteBody>) {
  const client = getBusinessClient();
  const { data, error, response } = await client.PATCH('/business/v1/policies/{id}', {
    params: { path: { id: policyId } },
    body: {
      ...body,
      rules: body.rules ? persistablePolicyRules(body.rules) : body.rules,
    } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function simulatePolicy(
  policyId: string,
  body: {
    mode?: 'basket' | 'historical';
    grandTotalMinor?: number;
    lineCount?: number;
    vertical?: string;
    categories?: string;
    evaluationTime?: string;
    consumedMinor?: number;
    dailyConsumedMinor?: number;
    weeklyConsumedMinor?: number;
    weekday?: string;
  },
) {
  const client = getBusinessClient();
  const historical = body.mode === 'historical';
  const { data, error, response } = await client.POST('/business/v1/policies/{id}/simulate', {
    params: { path: { id: policyId } },
    body: {
      mode: body.mode ?? 'basket',
      ...(historical
        ? {}
        : {
            basket: {
              grandTotalMinor: body.grandTotalMinor ?? 0,
              lineCount: body.lineCount ?? 1,
              vertical: body.vertical || undefined,
              restrictionClasses: body.categories
                ? body.categories
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : undefined,
            },
            evaluationTime: body.evaluationTime,
            weekday: body.weekday,
            consumedMinor: body.consumedMinor,
            dailyConsumedMinor: body.dailyConsumedMinor,
            weeklyConsumedMinor: body.weeklyConsumedMinor,
          }),
    } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function listPolicyVersions(policyId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/policies/{id}/versions', {
    params: { path: { id: policyId } },
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export function policyVersionsFromEnvelope(envelope: unknown): PolicyVersion[] {
  return readListFromEnvelope<PolicyVersion>(envelope, ['versions', 'items']);
}

export async function revertPolicy(policyId: string, body: { version?: number } = {}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/policies/{id}/revert', {
    params: { path: { id: policyId } },
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function loadPolicyEditor(policyId?: string) {
  const policies = policiesFromEnvelope(await listPolicies());
  if (!policyId) return { policies, policy: undefined };
  return { policies, policy: policies.find((policy) => policy.id === policyId) };
}

export async function listApprovalChains() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/approval-chains');
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function createApprovalChain(body: {
  name: string;
  scopeType?: string;
  scopeId?: string;
  steps?: ApprovalChainStep[];
  thresholdMinor?: number;
  escalationHours?: number;
  approverIds?: string[];
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approval-chains', {
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function updateApprovalChain(chainId: string, body: Record<string, unknown>) {
  const client = getBusinessClient();
  const { data, error, response } = await client.PATCH('/business/v1/approval-chains/{id}', {
    params: { path: { id: chainId } },
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function listDelegations() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/approval-delegations');
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function createDelegation(body: {
  fromEmployeeId?: string;
  toEmployeeId: string;
  startsAt: string;
  endsAt: string;
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approval-delegations', {
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function revokeDelegation(delegationId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.DELETE('/business/v1/approval-delegations/{id}', {
    params: { path: { id: delegationId } },
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function createRealtimeConnectionTicket(
  body: {
    purpose?: string;
    businessAccountId?: string;
  } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/realtime/connection-tickets', {
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function listApprovals() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/approvals');
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function getApproval(approvalId: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/approvals/{id}', {
    params: { path: { id: approvalId } },
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function approveRequest(
  approvalId: string,
  body: { note?: string; comment?: string } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approvals/{id}/approve', {
    params: { path: { id: approvalId } },
    body: { note: body.note ?? body.comment } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function rejectRequest(
  approvalId: string,
  body: { note?: string; comment?: string } = {},
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approvals/{id}/reject', {
    params: { path: { id: approvalId } },
    body: { note: body.note ?? body.comment } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function delegateApproval(
  approvalId: string,
  body: { toEmployeeId?: string; delegateeId?: string },
) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approvals/{id}/delegate', {
    params: { path: { id: approvalId } },
    body: {
      toEmployeeId: body.toEmployeeId ?? body.delegateeId,
    } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export function parseChainSteps(chain?: ApprovalChain | null): ApprovalChainStep[] {
  const raw = chain?.steps;
  if (Array.isArray(raw)) return raw as ApprovalChainStep[];
  return [];
}

export async function askApprovalQuestion(approvalId: string, question: string) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approvals/{id}/question', {
    params: { path: { id: approvalId } },
    body: { question } as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function withdrawApproval(approvalId: string, body: Record<string, unknown> = {}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/approvals/{id}/withdraw', {
    params: { path: { id: approvalId } },
    body: body as never,
  });
  if (!response.ok || error) throwSdkError(error);
  return data;
}

export async function loadApprovalDetail(approvalId: string) {
  const approval = approvalFromEnvelope(await getApproval(approvalId));
  return { approval };
}

export function ruleMoney(rules: PolicyRule[] | undefined, type: string): string {
  const v = rules?.find((r) => r.ruleType === type)?.moneyValueMinor;
  return v != null ? String(v) : '';
}

export function ruleNumeric(rules: PolicyRule[] | undefined, type: string): string {
  const v = rules?.find((r) => r.ruleType === type)?.numericValue;
  return v != null ? String(v) : '';
}

export function ruleText(rules: PolicyRule[] | undefined, type: string): string {
  return rules?.find((r) => r.ruleType === type)?.textValue ?? '';
}

export function ruleTime(
  rules: PolicyRule[] | undefined,
  type: string,
  field: 'timeFrom' | 'timeTo',
): string {
  return rules?.find((r) => r.ruleType === type)?.[field] ?? '';
}

export function summarizePolicyRules(policy: Policy): string {
  const parts: string[] = [];
  const rules = policy.rules ?? [];
  for (const r of rules) {
    if (r.ruleType === 'PER_ORDER_CAP' && r.moneyValueMinor != null) {
      parts.push(`£${(r.moneyValueMinor / 100).toFixed(2)}/order`);
    } else if (r.ruleType === 'DAILY_CAP' && r.moneyValueMinor != null) {
      parts.push(`£${(r.moneyValueMinor / 100).toFixed(2)}/day`);
    } else if (r.ruleType === 'WEEKLY_CAP' && r.moneyValueMinor != null) {
      parts.push(`£${(r.moneyValueMinor / 100).toFixed(2)}/week`);
    } else if (r.ruleType === 'MONTHLY_CAP' && r.moneyValueMinor != null) {
      parts.push(`£${(r.moneyValueMinor / 100).toFixed(2)}/mo`);
    } else if (r.ruleType === 'REQUIRES_APPROVAL_ABOVE' && r.moneyValueMinor != null) {
      parts.push(`approve > £${(r.moneyValueMinor / 100).toFixed(2)}`);
    } else if (r.ruleType === 'TIME_WINDOW') {
      const days = r.textValue ? ` ${r.textValue}` : '';
      parts.push(`${r.timeFrom}–${r.timeTo}${days}`);
    } else if (r.ruleType === 'DAY_OF_WEEK' && r.textValue) {
      parts.push(r.textValue);
    } else if (r.ruleType === 'MAX_ITEMS' && r.numericValue != null) {
      parts.push(`max ${r.numericValue} items`);
    } else if (r.ruleType === 'CATEGORY_BLOCK' && r.textValue) {
      parts.push(`exclude ${r.textValue}`);
    } else if (r.ruleType === 'VERTICAL_BLOCK' && r.textValue) {
      parts.push(`not ${r.textValue}`);
    }
  }
  return parts.join(' · ') || 'No rules yet';
}
